import { CheckCircle, Info, XCircle } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from "react";

export type ToastVariant = "success" | "error" | "info" | "neutral";

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

const variantStyles: Record<ToastVariant, string> = {
  success: "border-l-4 border-l-success",
  error: "border-l-4 border-l-error",
  info: "border-l-4 border-l-primary",
  neutral: "border-l-4 border-l-border",
};

const variantIcons: Record<ToastVariant, ReactNode> = {
  success: (
    <CheckCircle size={18} className="text-success shrink-0" weight="fill" />
  ),
  error: <XCircle size={18} className="text-error shrink-0" weight="fill" />,
  info: <Info size={18} className="text-primary shrink-0" weight="fill" />,
  neutral: <Info size={18} className="text-text-muted shrink-0" />,
};

function ToastSingle({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: number) => void;
}) {
  // auto-dismiss after 4s
  useEffect(() => {
    const t = setTimeout(() => onDismiss(item.id), 4000);
    return () => clearTimeout(t);
  }, [item.id, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.95 }}
      transition={{ duration: 0.25 }}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-bg-surface border border-border shadow-lg text-sm text-text-primary max-w-xs ${variantStyles[item.variant]}`}
      role="status"
      aria-live="polite"
    >
      {variantIcons[item.variant]}
      <span className="flex-1">{item.message}</span>
      <button
        onClick={() => onDismiss(item.id)}
        className="ml-auto text-text-muted hover:text-text-primary transition-colors cursor-pointer"
        aria-label="Dismiss notification"
      >
        ✕
      </button>
    </motion.div>
  );
}

// ─── Context-based toast API ───────────────────────────────────────────

interface ToastContextValue {
  show: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToastContext(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx)
    throw new Error("useToastContext must be used inside ToastProvider");
  return ctx;
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const show = useCallback(
    (message: string, variant: ToastVariant = "neutral") => {
      setToasts((prev) => [...prev, { id: ++nextId, message, variant }]);
    },
    [],
  );

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div
        className="fixed bottom-6 right-6 z-50 flex flex-col gap-2"
        aria-live="polite"
        aria-relevant="additions removals"
      >
        <AnimatePresence mode="popLayout">
          {toasts.map((item) => (
            <ToastSingle key={item.id} item={item} onDismiss={dismiss} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

// ─── Legacy hook API (still usable standalone) ─────────────────────────

export interface ToastState {
  open: boolean;
  message: string;
  show: (message: string) => void;
  dismiss: () => void;
}

export function useToast(): ToastState {
  const [state, setState] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const show = useCallback((message: string) => {
    setState({ open: true, message });
  }, []);

  const dismiss = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  return { ...state, show, dismiss };
}

export function ToastContainer({
  open,
  message,
  onDismiss,
}: {
  open: boolean;
  message: string;
  onDismiss: () => void;
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.25 }}
          className="fixed bottom-20 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl bg-bg-surface border border-border shadow-lg text-sm text-text-primary max-w-xs"
          role="status"
          aria-live="polite"
        >
          <span>{message}</span>
          <button
            onClick={onDismiss}
            className="ml-auto text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            aria-label="Dismiss notification"
          >
            ✕
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
